import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, DatabaseBackup, Loader2 } from 'lucide-react';
import { apiGet, apiPost } from '@/lib/api';
import { fmtTime, fmtBytes } from '@/lib/format';
import { order } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CountUp } from '@/components/CountUp';
import { DataTable, type Column } from '@/components/DataTable';
import { Drawer } from '@/components/Drawer';
import type {
  BackupStatus,
  BackupBatch,
  BackupBatchState,
  BackupDetail,
  BackupItem,
  BackupItemState,
  RestoreDrill,
  RestoreDrillState,
} from '@/types';

/* ── 狀態對照表，搬自原生版 backup.js ── */
const BATCH_LABEL: Record<BackupBatchState, string> = {
  queued: '排隊中', running: '執行中', done: '完成', partial: '部分完成', failed: '失敗',
};
const BATCH_VARIANT: Record<BackupBatchState, 'muted' | 'info' | 'success' | 'warn' | 'danger'> = {
  queued: 'muted', running: 'info', done: 'success', partial: 'warn', failed: 'danger',
};

const ITEM_LABEL: Record<BackupItemState, string> = {
  pending: '待處理', copied: '已複製', verified: '已驗證', failed: '失敗',
};
const ITEM_VARIANT: Record<BackupItemState, 'muted' | 'info' | 'success' | 'danger'> = {
  pending: 'muted', copied: 'info', verified: 'success', failed: 'danger',
};

const DRILL_LABEL: Record<RestoreDrillState, string> = {
  queued: '排隊中', running: '執行中', done: '完成', failed: '失敗',
};
const DRILL_VARIANT: Record<RestoreDrillState, 'info' | 'success' | 'danger'> = {
  queued: 'info', running: 'info', done: 'success', failed: 'danger',
};

/** 計算批次耗時 */
function elapsed(b: BackupBatch): string {
  if (!b.started_at || !b.finished_at) return '—';
  const ms = new Date(b.finished_at).getTime() - new Date(b.started_at).getTime();
  return `${(ms / 1000).toFixed(1)}s`;
}

/* ── 頁面元件 ── */

export default function BackupPage() {
  const queryClient = useQueryClient();

  /* 抽屜狀態 */
  const [detailId, setDetailId] = useState<number | null>(null);
  const [itemFilter, setItemFilter] = useState<string>('全部');
  const [backupNotice, setBackupNotice] = useState('');

  /* ── 批次清單 ── */
  const batchesQ = useQuery({
    queryKey: ['backups'],
    queryFn: () => apiGet<BackupBatch[]>('/api/v1/backups'),
    refetchInterval: (query) => query.state.data?.some((b) => b.state === 'queued' || b.state === 'running') ? 4000 : false,
  });

  /* ── 統計 ── */
  const statusQ = useQuery({
    queryKey: ['backup-status'],
    queryFn: () => apiGet<BackupStatus>('/api/v1/system/backup-status'),
    refetchInterval: () => batchesQ.data?.some((b) => b.state === 'queued' || b.state === 'running') ? 4000 : false,
  });
  const createMutation = useMutation({
    mutationFn: () => apiPost<{ item_count: number }>('/api/v1/backups', { limit: 500 }),
    onSuccess: (result) => {
      setBackupNotice(`已建立 ${result.item_count.toLocaleString()} 筆檔案的備份批次。`);
      void queryClient.invalidateQueries({ queryKey: ['backups'] });
      void queryClient.invalidateQueries({ queryKey: ['backup-status'] });
    },
  });

  useEffect(() => {
    if (batchesQ.data && !batchesQ.data.some((b) => b.state === 'queued' || b.state === 'running')) {
      void queryClient.invalidateQueries({ queryKey: ['backup-status'] });
    }
  }, [batchesQ.data, queryClient]);

  /* ── 批次詳情（選中時才載入）── */
  const detailQ = useQuery({
    queryKey: ['backups', detailId],
    queryFn: () => apiGet<BackupDetail>(`/api/v1/backups/${detailId}`),
    enabled: detailId !== null,
    refetchInterval: (query) => ['queued', 'running'].includes(query.state.data?.batch.state ?? '') ? 4000 : false,
  });

  /* ── 演練歷史（選中批次時載入）── */
  const drillsQ = useQuery({
    queryKey: ['backups', detailId, 'drills'],
    queryFn: () => apiGet<RestoreDrill[]>(`/api/v1/backups/${detailId}/restore-drills`),
    enabled: detailId !== null,
    refetchInterval: (query) => query.state.data?.some((d) => d.state === 'queued' || d.state === 'running') ? 4000 : false,
  });

  /* ── 觸發還原演練 ── */
  const drillMutation = useMutation({
    mutationFn: (batchId: number) =>
      apiPost(`/api/v1/backups/${batchId}/restore-drill`),
    onSuccess: () => {
      if (detailId !== null) {
        void queryClient.invalidateQueries({ queryKey: ['backups', detailId, 'drills'] });
      }
    },
  });

  /* ── 表格欄位 ── */
  const columns: Column<BackupBatch>[] = [
    { key: 'batch_key', header: '批次代號', cell: (b) => <span className="font-mono text-[11.5px]">{b.batch_key}</span> },
    {
      key: 'state', header: '狀態',
      cell: (b) => <Badge variant={BATCH_VARIANT[b.state]}>{BATCH_LABEL[b.state]}</Badge>,
    },
    {
      key: 'counts', header: '成功/失敗/總數',
      cell: (b) => `${b.ok_count} / ${b.fail_count} / ${b.item_count}`,
    },
    {
      key: 'size', header: '大小', align: 'right',
      className: 'tabular',
      cell: (b) => fmtBytes(b.total_bytes),
    },
    { key: 'elapsed', header: '耗時', cell: (b) => elapsed(b) },
    {
      key: 'manifest', header: 'Manifest',
      cell: (b) => (
        <span className="font-mono text-[11.5px]">
          {b.manifest_sha256 ? b.manifest_sha256.substring(0, 12) : '—'}
        </span>
      ),
    },
    {
      key: 'actions', header: '操作',
      cell: (b) => (
        <div className="flex gap-1.5">
          <Button size="sm" variant="secondary" onClick={() => { setItemFilter('全部'); setDetailId(b.id); }}>
            詳情
          </Button>
          {(b.state === 'done' || b.state === 'partial') && (
            <Button
              size="sm"
              variant="secondary"
              disabled={drillMutation.isPending}
              onClick={() => drillMutation.mutate(b.id)}
            >
              還原演練
            </Button>
          )}
        </div>
      ),
    },
  ];

  /* ── 統計卡片資料 ── */
  const s = statusQ.data;
  const oldestHrs = s?.oldest_unbacked_age_sec != null
    ? Math.round(s.oldest_unbacked_age_sec / 3600) : null;

  /* ── 抽屜內容 ── */
  const detail = detailQ.data;
  const batch = detail?.batch;
  const items = detail?.items ?? [];
  const filteredItems = itemFilter === '全部'
    ? items
    : items.filter((it) => it.state === itemFilter);

  const itemColumns: Column<BackupItem>[] = [
    { key: 'filename', header: '檔名', cell: (it) => it.filename ?? '—' },
    {
      key: 'state', header: '狀態',
      cell: (it) => <Badge variant={ITEM_VARIANT[it.state]}>{ITEM_LABEL[it.state]}</Badge>,
    },
    {
      key: 'size', header: '大小', align: 'right', className: 'tabular',
      cell: (it) => fmtBytes(it.bytes ?? it.actual_bytes),
    },
    {
      key: 'sha_src', header: '來源雜湊',
      cell: (it) => <span className="font-mono text-[11.5px]">{it.sha256_source?.substring(0, 12) ?? '—'}</span>,
    },
    {
      key: 'sha_dst', header: '目的雜湊',
      cell: (it) => <span className="font-mono text-[11.5px]">{it.sha256_dest?.substring(0, 12) ?? '—'}</span>,
    },
  ];

  const drills = drillsQ.data ?? [];

  return (
    <>
      {/* ── 統計卡片 ── */}
      {statusQ.isLoading ? (
        <div className="grid place-items-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : statusQ.error ? (
        <p className="py-6 text-center text-[13px] text-destructive">
          載入失敗：{statusQ.error instanceof Error ? statusQ.error.message : '未知錯誤'}
        </p>
      ) : s ? (
        <>
        {s.pending_total > 0 && (
          <section className={`reveal mb-4 rounded-lg border p-4 sm:p-5 ${s.backup_root_writable === false ? 'border-destructive/40 bg-destructive/5' : 'border-warn/40 bg-warn/5'}`} style={order(0)} aria-labelledby="backup-risk-title">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-sm bg-background"><AlertTriangle className="h-5 w-5 text-warn" aria-hidden="true" /></span>
                <div>
                  <h2 id="backup-risk-title" className="font-display text-base font-bold">{s.backup_root_writable === false ? '備份目的地無法寫入' : '待備份資料已形成保存風險'}</h2>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">目前有 {s.pending_total.toLocaleString()} 筆（{fmtBytes(s.pending_bytes)}）尚未備份{oldestHrs !== null ? `，最舊已等待 ${oldestHrs} 小時` : ''}。</p>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">負責人：系統管理員。{s.backup_root_writable === false ? '請在部署環境設定 BACKUP_ROOT，並確認容器掛載的目的目錄可寫入。' : '目的地已就緒，可直接建立最舊 500 筆的備份批次。'}</p>
                </div>
              </div>
              <Button className="shrink-0" disabled={s.backup_root_writable === false || createMutation.isPending} onClick={() => { setBackupNotice(''); createMutation.reset(); createMutation.mutate(); }}>
                {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />}
                建立備份批次
              </Button>
            </div>
            {backupNotice && <p role="status" className="callout mt-3 text-sm font-medium text-success-on">{backupNotice}</p>}
            {createMutation.error && <p role="alert" className="mt-3 text-sm text-destructive">建立失敗：{createMutation.error instanceof Error ? createMutation.error.message : '未知錯誤'}</p>}
          </section>
        )}
        <div className="reveal grid grid-cols-2 gap-4 xl:grid-cols-4" style={order(1)}>
          <Card>
            <CardHeader className="pb-2"><CardDescription>已保存</CardDescription></CardHeader>
            <CardContent><p className="text-2xl font-bold tabular"><CountUp value={s.stored_total} /> <span className="text-sm font-normal text-muted-foreground">筆</span></p></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardDescription>已備份</CardDescription></CardHeader>
            <CardContent><p className="text-2xl font-bold tabular"><CountUp value={s.backed_up_total} /> <span className="text-sm font-normal text-muted-foreground">筆</span></p></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardDescription>待備份</CardDescription></CardHeader>
            <CardContent>
              <p className="text-2xl font-bold tabular"><CountUp value={s.pending_total} /> <span className="text-sm font-normal text-muted-foreground">筆</span></p>
              <p className="mt-1 text-sm tabular text-muted-foreground">{fmtBytes(s.pending_bytes)}</p>
            </CardContent>
          </Card>
          <Card className={oldestHrs !== null && oldestHrs > 24 ? 'border-warn' : undefined}>
            <CardHeader className="pb-2"><CardDescription>最舊未備份</CardDescription></CardHeader>
            <CardContent>
              {s.oldest_unbacked_at ? (
                <div className="min-w-0 font-mono tabular">
                  <p className="break-words text-[13px] font-bold sm:text-base">{fmtTime(s.oldest_unbacked_at)}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{oldestHrs} 小時前</p>
                </div>
              ) : (
                <p className="text-2xl font-bold">—</p>
              )}
            </CardContent>
          </Card>
        </div>
        </>
      ) : null}

      {/* ── 批次清單 ── */}
      <Card className="reveal admin-data-card mt-6 overflow-hidden" style={order(2)}>
        <CardHeader>
          <div>
            <CardTitle>備份批次</CardTitle>
            <CardDescription>每次備份選取最舊的未備份檔案，逐檔複製並驗證雜湊</CardDescription>
          </div>
        </CardHeader>
        {batchesQ.data?.length === 0 && (
          <p className="px-6 pb-4 text-[12.5px] text-muted-foreground">
            尚未啟動備份。請系統管理員確認備份儲存位置並啟動備份工作；完成後，備份批次會顯示在此。
          </p>
        )}
        {batchesQ.error && <p className="px-6 pb-4 text-[12.5px] text-destructive">無法取得備份批次，請稍後重試。</p>}
        <DataTable<BackupBatch>
          rows={batchesQ.data ?? []}
          columns={columns}
          rowKey={(b) => String(b.id)}
          loading={batchesQ.isLoading}
          error={batchesQ.error}
          empty="尚無備份批次"
        />
      </Card>

      {/* ── 詳情抽屜 ── */}
      <Drawer
        open={detailId !== null}
        onOpenChange={(v) => { if (!v) setDetailId(null); }}
        title={batch ? `批次詳情 — ${batch.batch_key}` : '批次詳情'}
        wide
      >
        {detailQ.isLoading ? (
          <div className="grid place-items-center py-20">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : detailQ.error ? (
          <p className="py-6 text-center text-[13px] text-destructive">
            載入失敗：{detailQ.error instanceof Error ? detailQ.error.message : '未知錯誤'}
          </p>
        ) : batch ? (
          <div className="space-y-6">
            {/* 批次摘要 */}
            <div>
              <h3 className="text-base font-bold">批次 {batch.batch_key}</h3>
              <p className="mt-1 text-[13px]">
                狀態：<Badge variant={BATCH_VARIANT[batch.state]} className="mx-1">{BATCH_LABEL[batch.state]}</Badge>
                　成功 {batch.ok_count}　失敗 {batch.fail_count}　共 {batch.item_count} 筆
              </p>
              {batch.manifest_sha256 && (
                <p className="mt-1 font-mono text-[12px]">Manifest SHA-256：{batch.manifest_sha256}</p>
              )}
            </div>

            {/* 逐檔篩選 */}
            {items.length > 0 && (
              <>
                <div className="flex gap-2">
                  {(['全部', 'verified', 'failed', 'pending'] as const).map((key) => {
                    const labels: Record<string, string> = { '全部': '全部', verified: '已驗證', failed: '失敗', pending: '待處理' };
                    return (
                      <Button
                        key={key}
                        size="sm"
                        variant={itemFilter === key ? 'default' : 'secondary'}
                        onClick={() => setItemFilter(key)}
                      >
                        {labels[key]}
                      </Button>
                    );
                  })}
                </div>
                <DataTable<BackupItem>
                  rows={filteredItems}
                  columns={itemColumns}
                  rowKey={(it) => `${it.filename ?? ''}:${it.state}`}
                  empty="無符合篩選的項目"
                />
              </>
            )}

            {/* 還原演練區塊 */}
            <div>
              <h3 className="text-base font-bold">還原演練</h3>
              <Button
                size="sm"
                className="mt-2"
                disabled={
                  (batch.state !== 'done' && batch.state !== 'partial') ||
                  drillMutation.isPending
                }
                onClick={() => drillMutation.mutate(batch.id)}
              >
                {drillMutation.isPending ? '啟動中…' : '執行還原演練'}
              </Button>
              {drillMutation.isError && (
                <p className="mt-2 text-[13px] text-destructive">
                  啟動失敗：{drillMutation.error instanceof Error ? drillMutation.error.message : '未知錯誤'}
                </p>
              )}

              {/* 演練紀錄 */}
              {drillsQ.isLoading ? (
                <div className="mt-4 grid place-items-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : drillsQ.error ? (
                <p className="mt-4 text-[13px] text-destructive">載入演練紀錄失敗：{drillsQ.error instanceof Error ? drillsQ.error.message : '未知錯誤'}</p>
              ) : drills.length === 0 ? (
                <p className="mt-4 text-[13px] text-muted-foreground">尚無演練紀錄</p>
              ) : (
                <div className="mt-4 space-y-2">
                  {drills.map((d) => (
                    <Card key={d.id} className="p-3">
                      <div className="flex flex-wrap items-center gap-2 text-[13px]">
                        <Badge variant={DRILL_VARIANT[d.state]}>{DRILL_LABEL[d.state]}</Badge>
                        <span>共 {d.item_count} 筆，成功 {d.ok_count}，不符 {d.mismatch_count}</span>
                        {d.elapsed_ms != null && <span>耗時 {d.elapsed_ms}ms</span>}
                        {d.mismatch_count > 0 && (
                          <span className="text-destructive">⚠ 有不符合的檔案</span>
                        )}
                      </div>
                      {d.finished_at && (
                        <p className="mt-1 text-[12px] text-muted-foreground">
                          完成於 {fmtTime(d.finished_at)}
                        </p>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </Drawer>
    </>
  );
}
